/**
 * Chat Configuration
 *
 * Configuration settings for chat functionality including
 * default availability, feature flags, and access controls.
 */

import { isDevelopment as isDev } from '../env';

export interface ChatConfig {
  // Default state - whether chat is enabled by default (overridden by auth/subscription)
  defaultEnabled: boolean;

  // Whether to enforce authentication requirement
  requireAuth: boolean;

  // Whether to enforce subscription/payment requirement
  requireSubscription: boolean;

  // Allow override for development/testing
  allowOverride: boolean;

  // Development mode settings
  development?: {
    // Force enable chat regardless of auth/subscription
    forceEnabled?: boolean;
    // Skip auth requirement in development
    skipAuth?: boolean;
    // Skip subscription requirement in development
    skipSubscription?: boolean;
    // Completely bypass availability checks for performance testing
    bypassAvailabilityChecks?: boolean;
  };
}

/**
 * Default chat configuration
 *
 * By default, chat is disabled and requires both authentication
 * and an active subscription to access.
 */
export const DEFAULT_CHAT_CONFIG: ChatConfig = {
  // Chat is disabled by default - users must sign in and add payment
  defaultEnabled: false,

  // Require authentication to use chat
  requireAuth: true,

  // Require active subscription/payment to use chat
  requireSubscription: true,

  // Allow configuration override for special cases
  allowOverride: true,

  // Development settings (only apply in development mode)
  development: {
    // For development, you can force enable chat without auth/payment
    // Set to true to test chat functionality without full auth flow
    forceEnabled: false,
    skipAuth: false,
    skipSubscription: false,
    // Set to true to completely bypass availability checks for performance testing
    bypassAvailabilityChecks: false,
  },
};

// Memoized configuration to avoid recreating objects
let _cachedConfig: ChatConfig | null = null;

/**
 * Get effective chat configuration based on environment
 * Uses caching to avoid recreating config objects on every call
 */
export const getChatConfig = (): ChatConfig => {
  // Return cached config if already computed
  if (_cachedConfig) {
    return _cachedConfig;
  }

  const config = { ...DEFAULT_CHAT_CONFIG };

  // Apply development overrides if in development mode
  const isDevelopment = isDev;

  if (isDevelopment && config.development) {
    // In development, you can override requirements for testing
    if (config.development.forceEnabled) {
      config.defaultEnabled = true;
    }
    if (config.development.skipAuth) {
      config.requireAuth = false;
    }
    if (config.development.skipSubscription) {
      config.requireSubscription = false;
    }
  }

  // Cache the computed config
  _cachedConfig = config;
  return config;
};

/**
 * Helper function to determine if chat should be enabled
 * based on current auth/subscription state and configuration
 */
export const shouldEnableChat = (
  isAuthenticated: boolean,
  hasActiveSubscription: boolean,
  config: ChatConfig = getChatConfig()
): boolean => {
  // If default is disabled and requirements aren't met, return false
  if (!config.defaultEnabled) {
    if (config.requireAuth && !isAuthenticated) {
      return false;
    }
    if (config.requireSubscription && !hasActiveSubscription) {
      return false;
    }
  }

  // If we reach here, either default is enabled or all requirements are met
  return true;
};

/**
 * Export the current configuration
 */
export const chatConfig = getChatConfig();
